import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import AppChip from "../../design-system/components/AppChip";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppText from "../../design-system/components/AppText";
import { IconTrendingUp } from "../../design-system/icons";
import OccupancyChart from "./OccupancyChart";
import { buildOccupancyCurve } from "./occupancyModel";

// Bedarfsseite: die grosse Ansicht des Besetzungsverlaufs fuer genau einen Tag
// einer Abteilung — Kurve oben, darunter jede beitragende Schicht als eigener
// Balken. Die Balken haben eine eigene, unabhaengige 0-100%-Zeitachse in ihrer
// Track-Spalte statt an der Kurve pixelgenau ausgerichtet zu sein: die Kurve
// beginnt am linken Kartenrand, die Balken erst hinter der Schicht-Namensspalte
// links, ein echtes Ausrichten haette eine zweite, gleich breite Namensspalte
// ueber der Kurve erfordert. Als Gruppierung unter der Kurve reicht das.
const DemandOccupancyDialog = ({
  open, onClose, day, isDefaultView, shiftEntries,
}) => {
  const { t } = useTranslation();
  const curve = useMemo(() => buildOccupancyCurve(shiftEntries), [shiftEntries]);

  const intervalsByShiftId = useMemo(
    () => new Map(curve.intervals.map((interval) => [interval.shift.id, interval])),
    [curve.intervals]
  );

  const populatedShifts = useMemo(
    () => (shiftEntries || [])
      .filter((entry) => Number(entry.count) > 0 && intervalsByShiftId.has(entry.shift.id))
      .slice()
      .sort((a, b) => intervalsByShiftId.get(a.shift.id).start - intervalsByShiftId.get(b.shift.id).start),
    [shiftEntries, intervalsByShiftId]
  );

  if (!day) return null;

  const title = isDefaultView
    ? day.weekday
    : t("demand_ui.occupancy_dialog_title", { weekday: day.weekday, date: day.label });
  const span = Math.max(1, curve.rangeEnd - curve.rangeStart);

  return (
    <AppDialog open={open} onClose={onClose} size="lg" className="demand-v2__occupancy-dialog">
      <AppDialogHeader
        title={`${title} · ${t("demand_ui.occupancy_dialog_subtitle")}`}
        visual={<IconTrendingUp />}
      />
      <AppDialogBody className="demand-v2__occupancy-dialog-body">
        {curve.segments.length ? (
          <>
            <div className="demand-v2__occupancy-legend">
              <span className="demand-v2__occupancy-legend-swatch" aria-hidden="true" />
              <AppText variant="body-2">{t("demand_ui.occupancy_legend_present")}</AppText>
              <AppText variant="label-3" tone="tertiary" className="demand-v2__occupancy-legend-caption">
                {t("demand_ui.occupancy_step_caption")}
              </AppText>
            </div>

            <OccupancyChart curve={curve} variant="detail" />

            <div className="demand-v2__occupancy-breakdown">
              {populatedShifts.map(({ shift, count }) => {
                const interval = intervalsByShiftId.get(shift.id);
                const left = ((interval.start - curve.rangeStart) / span) * 100;
                const widthPct = ((interval.end - interval.start) / span) * 100;
                return (
                  <div key={shift.id} className="demand-v2__occupancy-breakdown-row">
                    <div className="demand-v2__occupancy-breakdown-label">
                      <AppText as="strong" variant="label-2" truncate>{shift.name}</AppText>
                      <AppChip size="sm" variant="neutral" appearance="semiFilled">
                        {shift.shorthand_symbol}
                      </AppChip>
                    </div>
                    <div className="demand-v2__occupancy-breakdown-track">
                      <div
                        className="demand-v2__occupancy-breakdown-bar"
                        style={{
                          "--demand-shift-accent": shift.color_hex || "var(--ds-color-brand-default)",
                          left: `${left}%`,
                          width: `${widthPct}%`,
                        }}
                      >
                        <AppText as="span" variant="label-2" className="demand-v2__occupancy-breakdown-count">
                          {count}
                        </AppText>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <AppText variant="body-2" tone="secondary">{t("demand_ui.occupancy_breakdown_empty")}</AppText>
        )}
      </AppDialogBody>
    </AppDialog>
  );
};

export default DemandOccupancyDialog;
